import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, MapPin } from 'lucide-react';
import type { EntityAttachment, FeatureRequest, FeatureRequestType, FeatureRequestArea, RequestOriginContext } from '@pcp/shared';
import { FEATURE_REQUEST_AREAS } from '@pcp/shared';
import {
  createFeatureRequest, updateFeatureRequest, uploadRequestAttachment, deleteRequestAttachment,
  getFeatureRequest, ensureRequestFeedbackBoard, uploadFeedbackImage, ApiError,
} from '../../lib/api';
import { canRetakeWithDisplayMedia, captureViaDisplayMedia } from '../../lib/pageCapture';
import { useOriginContext, originContextLabel } from '../../lib/useOriginContext';
import { submitErrorMessage } from '../../lib/submitError';
import { draftKey, readDraft, writeDraft, clearDraft } from '../../lib/useDraft';
import { useAuthStore } from '../../lib/authStore';
import { useToastStore } from '../../lib/stores';
import { useSlideNavigate } from '../../lib/useSlideNavigate';
import { BottomSheet } from '../BottomSheet';
import { WorkingSpinner } from '../WorkingSpinner';
import { RequestAttachmentField } from './RequestAttachmentField';
import { RequestMarkdownEditor } from './RequestMarkdownEditor';
import { EditConflictBanner } from '../StaleDataBanner';
import { useEditConflictGuard } from '../../lib/editRegistry';

/** The attachment id lives in the server-built downloadUrl (/api/requests/attachments/<id>?…). */
const attIdFromUrl = (downloadUrl: string): string | null => {
  const m = /\/attachments\/([^/?#]+)/.exec(downloadUrl);
  return m ? m[1] : null;
};

/** Create OR edit a feature/bug request (PCP-local), with attachments. Pass
 *  `request` to edit — allowed for the author while it's New/Planned, or any holder
 *  of requests.manage; omit it to create. On create, staged files upload after the
 *  request exists; on edit they upload immediately. */
export function CreateRequestModal({ open, onClose, onCreated, request, initialCapture = null }: {
  open: boolean;
  onClose: () => void;
  onCreated?: (id: string) => void;
  request?: FeatureRequest | null;
  /** A screenshot of the page the user was on when they opened this, staged by
   *  the capture store. On create it becomes the first screenshot on the new
   *  request's feedback board — not an attachment, because a board screenshot can
   *  be pinned to and an attachment can only be downloaded. (f-01928) */
  initialCapture?: File | null;
}) {
  const editing = !!request;
  const addToast = useToastStore((s) => s.addToast);
  const slideNavigate = useSlideNavigate();
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [type, setType] = useState<FeatureRequestType>('feature');
  // Area is AI-classified on create (server-side); editable here to fix a misclassification.
  const [area, setArea] = useState<FeatureRequestArea>('General');
  // Optional "how to use" guide — how to see the fix / use the feature.
  const [howTo, setHowTo] = useState('');
  const [staged, setStaged] = useState<File[]>([]);
  const [existing, setExisting] = useState<EntityAttachment[]>([]);
  const [attBusy, setAttBusy] = useState(false);
  // Origin context (create mode): freeze "where the user was" when the sheet opens;
  // the user can include it (default) or exclude it.
  const liveContext = useOriginContext();
  // Always-current mirror so the open-effect reads the freshest "where the user
  // was" even though it only fires on the open flip (the modal stays mounted, so
  // the route can change between opens).
  const liveContextRef = useRef(liveContext);
  liveContextRef.current = liveContext;
  const [ctx, setCtx] = useState<RequestOriginContext | null>(null);
  const [includeCtx, setIncludeCtx] = useState(true);
  // The staged page screenshot. Held in state (not read from the prop) because
  // Retake replaces it, and unticking drops it entirely. (f-01928)
  const [shot, setShot] = useState<File | null>(null);
  const [includeShot, setIncludeShot] = useState(true);
  const [retaking, setRetaking] = useState(false);

  // localStorage draft (CREATE mode only) so an accidental close / refresh / crash
  // doesn't lose a typed request. This modal stays mounted and re-seeds on open,
  // so the restore is folded into the seed effect (not the useDraft mount hook).
  // The create-mode draft key. It used to be scoped per surface, because the
  // Requests page, the notification tray and the capture launcher each mounted
  // their own instance and a submit in one could resurrect a stale draft in
  // another. There is one instance now (b-01933), so there is one key — and the
  // old 'page' segment is kept verbatim rather than tidied away, so a draft
  // somebody had in progress survives this deploy.
  const draftK = draftKey(['request-new', 'page', userId]);

  // Seed fields when the sheet opens (or the edited request changes).
  useEffect(() => {
    if (!open) return;
    if (editing && request) {
      setTitle(request.title ?? '');
      setBody(request.body ?? '');
      setType(request.type ?? 'feature');
      setArea(request.area ?? 'General');
      setHowTo(request.howTo ?? '');
      setExisting(request.attachments ?? []);
    } else {
      // Create: restore any saved draft so nothing typed is lost on reopen.
      let saved: { title?: string; body?: string } = {};
      try { saved = JSON.parse(readDraft(draftK) ?? '{}'); } catch { /* ignore */ }
      setTitle(saved.title ?? '');
      setBody(saved.body ?? '');
      setType('feature');
      setHowTo('');
      setExisting([]);
      setCtx(liveContextRef.current); // freeze where the user was at open time
      setIncludeCtx(true);
      setShot(initialCapture);
      setIncludeShot(true);
    }
    setStaged([]);
  }, [open, request?.id, initialCapture]); // eslint-disable-line react-hooks/exhaustive-deps

  /** An object URL for the thumbnail, revoked when the shot changes or the sheet
   *  unmounts — a leaked one pins the whole PNG in memory for the session. */
  const shotUrl = useMemo(() => (shot ? URL.createObjectURL(shot) : null), [shot]);
  useEffect(() => () => { if (shotUrl) URL.revokeObjectURL(shotUrl); }, [shotUrl]);

  /** The true-screenshot escalation, for the cases a DOM rasteriser cannot reach.
   *  Prompts every time, which is exactly why it is a button and not the default. */
  const retake = async () => {
    setRetaking(true);
    try {
      const better = await captureViaDisplayMedia();
      // Null also means "the user cancelled the picker", which is not a failure —
      // keep the shot they already have rather than clearing it.
      if (better) { setShot(better); setIncludeShot(true); }
    } finally {
      setRetaking(false);
    }
  };

  // Debounced autosave of the create-mode draft while the sheet is open.
  useEffect(() => {
    if (!open || editing) return;
    const t = setTimeout(() => {
      if (title.trim() || body.trim()) writeDraft(draftK, JSON.stringify({ title, body }));
      else clearDraft(draftK);
    }, 600);
    return () => clearTimeout(t);
  }, [open, editing, title, body, draftK]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['feature-requests'] });
    queryClient.invalidateQueries({ queryKey: ['feature-request-stats'] });
    if (request?.id) queryClient.invalidateQueries({ queryKey: ['feature-request', request.id] });
  };

  const save = useMutation({
    mutationFn: async () => {
      if (editing && request) {
        // Authors may edit title/body/attachments; changing type needs manage
        // (the server also strips it), so only send type when allowed.
        const patch: { title: string; body: string; howTo: string | null; type?: FeatureRequestType; area?: FeatureRequestArea } = { title: title.trim(), body: body.trim(), howTo: howTo.trim() || null, area };
        if (request.canManage) patch.type = type;
        await updateFeatureRequest(request.id, patch);
        return { id: request.id, boardId: null };
      }
      const created = await createFeatureRequest({ title: title.trim(), body: body.trim() || undefined, howTo: howTo.trim() || undefined, type, originContext: includeCtx ? ctx : null });
      // The request is already created, so surface per-file upload failures
      // without failing the whole create.
      let failed = 0;
      for (const f of staged) {
        try { await uploadRequestAttachment(created.id, f); } catch { failed++; }
      }
      if (failed) addToast({ type: 'error', message: `${failed} attachment${failed === 1 ? '' : 's'} could not be uploaded` });

      // The page screenshot goes on the request's BOARD, not into attachments: a
      // board screenshot can have comments pinned to exact pixels of it, which is
      // the entire reason to capture the page rather than describe it. Same
      // posture as the attachments above — the request is already filed, so a
      // failure here is a toast, never a lost request. (f-01928)
      let boardId: string | null = null;
      if (shot && includeShot) {
        try {
          const board = await ensureRequestFeedbackBoard(created.id);
          await uploadFeedbackImage(board.id, shot, { x: 0, y: 0 });
          boardId = board.id;
        } catch {
          addToast({ type: 'error', message: 'The request was created, but the screenshot could not be added to its board.' });
        }
      }
      return { id: created.id, boardId };
    },
    onSuccess: ({ id, boardId }) => {
      if (!editing) clearDraft(draftK); // drop the saved create-mode draft
      addToast({
        type: 'success',
        message: editing
          ? 'Request updated'
          : boardId
            ? `${type === 'bug' ? 'Bug' : 'Feature'} request created — screenshot added to its board`
            : `${type === 'bug' ? 'Bug' : 'Feature'} request created`,
      });
      invalidate();
      onCreated?.(id);
      onClose();
      if (!editing) {
        // Go to the request that was just filed. (b-01931)
        //
        // Leaving the author where they were meant the thing they had just
        // written — and the screenshot just captured onto its board — was three
        // navigations away: Requests, find it in the list, open it, switch tabs.
        // That is backwards for a shortcut whose whole point is "say something
        // about what is on screen, now".
        //
        // Straight to the BOARD when there is one, because annotating is what the
        // screenshot was captured for; to Details otherwise, where what you just
        // wrote is the thing worth checking. Same slide the requests list uses to
        // open a row, and the detail chunk is warmed first so it lands on a
        // loaded page.
        //
        // Warmed by importing the module directly rather than through App's
        // `RequestDetail` lazy page, which is what the requests LIST does. This
        // component is reachable EAGERLY (App → Layout → AppTopbar → the capture
        // launcher → here), so importing App back would close a real import cycle;
        // the same specifier resolves to the same chunk with none.
        void import('../../pages/RequestDetail');
        slideNavigate(`/requests/${id}${boardId ? '?tab=feedback' : ''}`);
      }
    },
    onError: (e: unknown) => {
      addToast({ type: 'error', message: submitErrorMessage(e, 'Could not save request') });
      // A permission denial (e.g. the request advanced to In progress / closed while
      // the editor was open) won't succeed on retry — close so the author isn't stuck
      // re-submitting the same 403 and losing their place.
      if (e instanceof ApiError && e.statusCode === 403) onClose();
    },
  });

  const close = () => { if (!save.isPending && !attBusy) onClose(); };

  // Edit-conflict guard: warn if someone else changes THIS request while editing it.
  const isDirty = open && editing && !!request && (
    title !== (request.title ?? '') ||
    body !== (request.body ?? '') ||
    type !== (request.type ?? 'feature') ||
    area !== (request.area ?? 'General') ||
    howTo !== (request.howTo ?? '')
  );
  const conflict = useEditConflictGuard('request', request?.id, isDirty);
  const reloadLatest = async () => {
    if (request) {
      try {
        const fresh = await getFeatureRequest(request.id);
        setTitle(fresh.title ?? '');
        setBody(fresh.body ?? '');
        setType(fresh.type ?? 'feature');
        setArea(fresh.area ?? 'General');
        setHowTo(fresh.howTo ?? '');
        setExisting(fresh.attachments ?? []);
      } catch { /* ignore — invalidation will refresh the board behind */ }
    }
    conflict.acknowledge();
  };

  // Edit mode uploads immediately to the existing request; create mode stages.
  const addFiles = async (files: File[]) => {
    if (editing && request) {
      setAttBusy(true);
      let failed = 0;
      const added: EntityAttachment[] = [];
      for (const f of files) {
        try { added.push(await uploadRequestAttachment(request.id, f)); } catch { failed++; }
      }
      if (added.length) { setExisting((prev) => [...prev, ...added]); invalidate(); }
      if (failed) addToast({ type: 'error', message: `${failed} attachment${failed === 1 ? '' : 's'} could not be uploaded` });
      setAttBusy(false);
    } else {
      setStaged((prev) => [...prev, ...files]);
    }
  };

  const removeExisting = async (a: EntityAttachment) => {
    if (!request) return;
    const attId = attIdFromUrl(a.downloadUrl);
    if (!attId) return;
    setAttBusy(true);
    try {
      await deleteRequestAttachment(request.id, attId);
      setExisting((prev) => prev.filter((x) => x.downloadUrl !== a.downloadUrl));
      invalidate();
    } catch (e) {
      addToast({ type: 'error', message: e instanceof Error ? e.message : 'Could not remove attachment' });
    } finally {
      setAttBusy(false);
    }
  };

  return (
    <BottomSheet
      open={open}
      onClose={close}
      title={editing ? 'Edit request' : 'New request'}
      width={560}
      footer={
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={close} disabled={save.isPending}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={!title.trim() || save.isPending || attBusy} onClick={() => save.mutate()}>
            {save.isPending ? <WorkingSpinner /> : editing ? 'Save changes' : 'Create request'}
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <EditConflictBanner
          show={conflict.conflict}
          coarse={conflict.coarse}
          deleted={conflict.deleted}
          onKeep={conflict.acknowledge}
          onReload={reloadLatest}
        />
        {/* Type is chosen at creation; changing it later requires requests.manage. */}
        {(!editing || request?.canManage) && (
          <div>
            <div className="settings-row-label" style={{ marginBottom: 4 }}>Type</div>
            <div className="settings-subnav" role="radiogroup" aria-label="Request type">
              <button type="button" role="radio" aria-checked={type === 'feature'} className={`settings-subnav-chip${type === 'feature' ? ' active' : ''}`} onClick={() => setType('feature')}>Feature</button>
              <button type="button" role="radio" aria-checked={type === 'bug'} className={`settings-subnav-chip${type === 'bug' ? ' active' : ''}`} onClick={() => setType('bug')}>Bug</button>
            </div>
          </div>
        )}
        {/* Area: AI-assigned on create (no picker), editable here to correct a misclassification. */}
        {editing ? (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="settings-row-label">Area</span>
            <select className="select" value={area} onChange={(e) => setArea(e.target.value as FeatureRequestArea)} disabled={save.isPending} style={{ width: '100%' }}>
              {FEATURE_REQUEST_AREAS.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </label>
        ) : (
          <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
            Area is auto-assigned, editable later.
          </p>
        )}
        {/* ── Context (create mode) ──────────────────────────────────
            Where the user was, and what it looked like. ONE block rather than two
            stacked checkboxes because they answer the same question with two kinds
            of evidence — and because a screenshot of your own screen is something
            you should be able to see and reconsider before it is filed. Both
            default to on; either can be dropped. (f-01928) */}
        {!editing && (shot || (ctx && originContextLabel(ctx))) && (
          <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: 'var(--bg-elevated)' }}>
            <div className="settings-row-label" style={{ padding: 'var(--space-1) var(--space-2)', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-tertiary)' }}>
              Context
            </div>

            {ctx && originContextLabel(ctx) && (
              <label
                title={ctx.route}
                style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer',
                  padding: 'var(--space-2)', fontSize: 'var(--text-sm)',
                  opacity: includeCtx ? 1 : 0.55,
                }}
              >
                <input type="checkbox" checked={includeCtx} onChange={(e) => setIncludeCtx(e.target.checked)} />
                <MapPin size={14} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  Include where I was: <strong>{originContextLabel(ctx)}</strong>
                </span>
              </label>
            )}

            {shot && (
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                  padding: 'var(--space-2)', borderTop: ctx && originContextLabel(ctx) ? '1px solid var(--border-subtle)' : undefined,
                  fontSize: 'var(--text-sm)', opacity: includeShot ? 1 : 0.55,
                }}
              >
                <input
                  type="checkbox"
                  checked={includeShot}
                  aria-label="Include a screenshot of the page"
                  onChange={(e) => setIncludeShot(e.target.checked)}
                />
                {shotUrl && (
                  <img
                    src={shotUrl}
                    alt=""
                    style={{
                      width: 64, height: 40, objectFit: 'cover', objectPosition: 'top left',
                      borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', flexShrink: 0,
                    }}
                  />
                )}
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, gap: 1 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Camera size={13} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                    <strong>Screenshot of this page</strong>
                  </span>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                    Goes on the request&rsquo;s feedback board, where it can be commented on.
                  </span>
                </span>
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--space-1)', flexShrink: 0 }}>
                  {canRetakeWithDisplayMedia() && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => void retake()}
                      disabled={retaking || save.isPending}
                      title="Take a true screen capture instead — your browser will ask which tab to share"
                    >
                      {retaking ? <WorkingSpinner /> : 'Retake'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setShot(null)}
                    disabled={save.isPending}
                  >
                    Remove
                  </button>
                </span>
              </div>
            )}
          </div>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="settings-row-label">Title</span>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short, specific summary" autoFocus style={{ width: '100%' }} />
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="settings-row-label">Details</span>
          <RequestMarkdownEditor value={body} onChange={setBody} disabled={save.isPending} ariaLabel="Details" height={480} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="settings-row-label">How to use <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(optional)</span></span>
          <RequestMarkdownEditor
            value={howTo}
            onChange={setHowTo}
            disabled={save.isPending}
            ariaLabel="How to use"
            height={192}
            placeholder="Steps to see the fix or use the feature (shown to everyone on the request)."
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="settings-row-label">Attachments</span>
          <RequestAttachmentField
            existing={editing ? existing : []}
            staged={editing ? [] : staged}
            onAddFiles={addFiles}
            onRemoveStaged={(i) => setStaged((prev) => prev.filter((_, idx) => idx !== i))}
            onRemoveExisting={editing ? removeExisting : undefined}
            uploading={attBusy}
            disabled={save.isPending}
          />
        </div>
      </div>
    </BottomSheet>
  );
}
